import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import Logo from "../assets/artefactly-logo.svg";

// Below this width the inline links collapse into a hamburger menu.
const MOBILE = 640;

const NavbarView = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the menu whenever the route changes (e.g. after tapping a link).
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <Nav>
      <Bar>
        <LogoContainer to="/" aria-label="Artefactly home">
          <LogoImage src={Logo} alt="Artefactly Logo" />
        </LogoContainer>

        <CenterText>a r t e f a c t l y .</CenterText>

        <Right>
          <NavLinks $open={open} id="primary-nav">
            <NavLink to="/" $active={location.pathname === "/"}>
              Home
            </NavLink>
            <NavLink to="/gallery" $active={location.pathname === "/gallery"}>
              Gallery
            </NavLink>
            <NavLink
              to="/collections"
              $active={location.pathname.startsWith("/collections")}
            >
              Collections
            </NavLink>
            <NavLink to="/museum" $active={location.pathname.startsWith("/museum")}>
              Museum
            </NavLink>
          </NavLinks>

          <Hamburger
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="primary-nav"
            $open={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </Hamburger>
        </Right>
      </Bar>
    </Nav>
  );
};

export default NavbarView;

const Nav = styled.nav`
  background-color: var(--bg-elevated);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
`;

// Three-track grid: the logo sits left, the nav group right, and the wordmark
// in a center track that stays optically centred on the bar no matter how much
// wider one side is than the other. (A flex space-between put the wordmark at
// the midpoint of the leftover space, i.e. skewed toward the narrower side.)
const Bar = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  padding: 12px 30px;
  min-height: var(--nav-h);
  box-sizing: border-box;

  @media (max-width: ${MOBILE}px) {
    padding: 8px 16px;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  justify-self: start;
  flex-shrink: 0;
`;

const LogoImage = styled.img`
  height: 56px;
  width: auto;
  display: block;

  @media (max-width: ${MOBILE}px) {
    height: 36px;
  }
`;

const CenterText = styled.div`
  font-weight: bold;
  color: var(--paper);
  text-align: center;
  justify-self: center;
  line-height: 1;
  letter-spacing: 0.08em;
  font-size: clamp(14px, 1.6vw + 6px, 22px);
  white-space: nowrap;

  @media (max-width: ${MOBILE}px) {
    font-size: clamp(11px, 3vw, 15px);
    letter-spacing: 0.03em;
  }

  @media (max-width: 360px) {
    display: none;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-self: end;
  flex-shrink: 0;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 28px;

  @media (max-width: ${MOBILE}px) {
    position: absolute;
    top: 100%;
    right: 0;
    flex-direction: column;
    gap: 0;
    min-width: 180px;
    background: var(--bg-elevated);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.4);
    border-bottom-left-radius: var(--radius);
    /* Scroll rather than clip: the menu never hides an item, and on a short
       screen (or with more tabs) it caps at the viewport and scrolls. */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* Collapse/expand animation */
    max-height: ${(p) => (p.$open ? "70vh" : "0")};
    opacity: ${(p) => (p.$open ? "1" : "0")};
    pointer-events: ${(p) => (p.$open ? "auto" : "none")};
    transition: max-height var(--dur) var(--ease), opacity var(--dur-fast) ease;
  }
`;

const NavLink = styled(Link)`
  color: ${(p) => (p.$active ? "var(--accent)" : "var(--paper)")};
  text-decoration: none;
  font-weight: bold;
  font-size: 18px;
  transition: color var(--dur-fast) ease;

  &:hover {
    color: var(--accent);
  }

  @media (max-width: ${MOBILE}px) {
    font-size: 17px;
    padding: 16px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);

    &:hover {
      background: rgba(255, 255, 255, 0.04);
    }
  }
`;

// Hidden on desktop; the classic three-line icon that morphs into an X.
const Hamburger = styled.button`
  display: none;
  position: relative;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;

  span {
    display: block;
    height: 2px;
    width: 100%;
    background: var(--paper);
    border-radius: 2px;
    transition: transform var(--dur-fast) var(--ease),
      opacity var(--dur-fast) ease;
  }

  span + span {
    margin-top: 6px;
  }

  /* Morph to an X when open */
  span:nth-child(1) {
    transform: ${(p) => (p.$open ? "translateY(8px) rotate(45deg)" : "none")};
  }
  span:nth-child(2) {
    opacity: ${(p) => (p.$open ? "0" : "1")};
  }
  span:nth-child(3) {
    transform: ${(p) => (p.$open ? "translateY(-8px) rotate(-45deg)" : "none")};
  }

  @media (max-width: ${MOBILE}px) {
    display: block;
  }
`;
